import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEdit2, FiTrash2, FiPlay, FiRotateCw, FiClock, FiFileText, FiAward } from 'react-icons/fi';
import { useData } from '../../contexts/DataContext';
import { useToast } from '../../contexts/ToastContext';
import './QuizDetailView.css';

/**
 * QuizDetailView - Detailed info view for a selected quiz
 * 
 * Shown in the workspace when a quiz is selected from the sidebar list.
 * Features: editable title, delete, source documents, attempt history, primary action button.
 */
const QuizDetailView = ({ quiz, onStartQuiz, onSelectAttempt, onBack }) => {
  const { getQuizAttempts, getDocumentById, updateQuizSet, deleteQuizSet, clearCache } = useData();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [attempts, setAttempts] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [attemptsLoading, setAttemptsLoading] = useState(true);
  const [docsLoading, setDocsLoading] = useState(true);
  
  // Inline rename state
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(quiz.title || '');
  const [title, setTitle] = useState(quiz.title || `Quiz ${quiz.id}`);

  // Load attempt history
  useEffect(() => {
    if (!quiz?.id) return;
    let cancelled = false;
    
    const loadAttempts = async () => {
      setAttemptsLoading(true);
      try {
        const data = await getQuizAttempts(quiz.id);
        if (cancelled) return;
        // API may return array directly or wrapped object
        const attemptList = Array.isArray(data) ? data : (data?.attempts || []);
        // Sort newest first
        attemptList.sort((a, b) => new Date(b.createdAt || b.startedAt) - new Date(a.createdAt || a.startedAt));
        setAttempts(attemptList);
      } catch (err) {
        if (cancelled) return;
        // 404 expected if no attempts
        if (err?.status !== 404) {
          console.warn('Failed to load quiz attempts:', err);
        }
        setAttempts([]);
      } finally {
        if (!cancelled) setAttemptsLoading(false);
      }
    };

    loadAttempts();
    return () => { cancelled = true; };
  }, [quiz?.id, getQuizAttempts]);

  // Load source documents
  useEffect(() => {
    if (!quiz?.documentIds || quiz.documentIds.length === 0) {
      setDocsLoading(false);
      return;
    }
    let cancelled = false;

    const loadDocs = async () => {
      setDocsLoading(true);
      try {
        const docPromises = quiz.documentIds.map(docId => getDocumentById(docId));
        const docs = await Promise.all(docPromises);
        if (!cancelled) setDocuments(docs.filter(Boolean));
      } catch (err) {
        console.error('Failed to load documents:', err);
        if (!cancelled) setDocuments([]);
      } finally {
        if (!cancelled) setDocsLoading(false);
      }
    };

    loadDocs();
    return () => { cancelled = true; };
  }, [quiz?.documentIds, getDocumentById]);

  // Sync title when quiz prop changes
  useEffect(() => {
    setTitle(quiz.title || `Quiz ${quiz.id}`);
    setEditTitle(quiz.title || '');
  }, [quiz.title, quiz.id]);

  const handleSaveTitle = useCallback(async () => {
    const trimmed = editTitle.trim();
    if (!trimmed || trimmed === title) {
      setIsEditing(false);
      setEditTitle(title);
      return;
    }

    try {
      await updateQuizSet(quiz.id, { title: trimmed });
      setTitle(trimmed);
      clearCache('quiz-sets');
      addToast({ intent: 'success', message: 'Quiz renamed' });
    } catch (err) {
      console.error('Failed to rename quiz:', err);
      addToast({ intent: 'danger', message: 'Failed to rename quiz' });
      setEditTitle(title);
    }
    setIsEditing(false);
  }, [editTitle, title, quiz.id, updateQuizSet, clearCache, addToast]);

  const handleDelete = useCallback(async () => {
    if (!window.confirm('Are you sure you want to delete this quiz?')) return;

    try {
      await deleteQuizSet(quiz.id);
      clearCache('quiz-sets');
      addToast({ intent: 'success', message: 'Quiz deleted' });
      if (onBack) onBack();
    } catch (err) {
      console.error('Failed to delete quiz:', err);
      addToast({ intent: 'danger', message: 'Failed to delete quiz' });
    }
  }, [quiz.id, deleteQuizSet, clearCache, addToast, onBack]);

  const hasCompletedAttempts = attempts.some(a => a.completedAt || a.completed_at || a.scorePercent != null || a.score_percent != null);

  const formatRelativeDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    if (diffMs < 0) return 'just now';
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const formatShortDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="quiz-detail-view">
      {/* Toolbar: Delete */}
      <div className="qdv-toolbar">
        <button className="qdv-delete-btn" onClick={handleDelete} title="Delete quiz">
          <FiTrash2 size={14} />
        </button>
      </div>

      {/* Header Card: Title + Chips + CTA */}
      <div className="qdv-header-card">
        {isEditing ? (
          <div className="qdv-edit-title">
            <input
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveTitle();
                if (e.key === 'Escape') { setIsEditing(false); setEditTitle(title); }
              }}
              onBlur={handleSaveTitle}
              className="qdv-edit-input"
              autoFocus
            />
          </div>
        ) : (
          <h2 className="qdv-title" onClick={() => { setIsEditing(true); setEditTitle(title); }}>
            {title}
            <FiEdit2 className="qdv-title-edit-icon" size={14} />
          </h2>
        )}

        {/* Chip Badges */}
        <div className="qdv-chips">
          <span className="qdv-chip">
            <FiFileText size={13} />
            {quiz.numQuestions || '?'} Questions
          </span>
          {quiz.difficultyLevel && (
            <span className={`qdv-chip qdv-chip--${quiz.difficultyLevel.toLowerCase()}`}>
              <FiAward size={13} />
              {quiz.difficultyLevel}
            </span>
          )}
          {documents.length > 0 && (
            <span className="qdv-chip">
              📄 {documents.length} Document{documents.length !== 1 ? 's' : ''}
            </span>
          )}
          <span className="qdv-chip">
            <FiClock size={13} />
            {formatRelativeDate(quiz.createdAt)}
          </span>
        </div>

        {/* Primary Action */}
        <button
          className="qdv-primary-btn"
          onClick={() => onStartQuiz && onStartQuiz(quiz, hasCompletedAttempts ? 'retake' : 'start')}
        >
          {hasCompletedAttempts ? (
            <><FiRotateCw size={18} /> Retake Quiz</>
          ) : (
            <><FiPlay size={18} /> Start Quiz</>
          )}
        </button>
      </div>

      {/* Two-column grid: Source Documents + Attempt History */}
      <div className="qdv-grid">
        {/* Source Documents */}
        <div className="qdv-section">
          <h3 className="qdv-section-title">Source Documents</h3>
          {docsLoading ? (
            <div className="qdv-loading-inline">Loading documents...</div>
          ) : documents.length === 0 ? (
            <div className="qdv-empty-inline">No source documents</div>
          ) : (
            <div className="qdv-doc-list">
              {documents.map(doc => (
                <div
                  key={doc.id}
                  className="qdv-doc-item"
                  onClick={() => navigate(`/document/${doc.id}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/document/${doc.id}`); } }}
                >
                  <span className="qdv-doc-icon">📄</span>
                  <span className="qdv-doc-name">{doc.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Attempt History */}
        <div className="qdv-section">
          <h3 className="qdv-section-title">Attempt History</h3>
          {attemptsLoading ? (
            <div className="qdv-loading-inline">Loading attempts...</div>
          ) : attempts.length === 0 ? (
            <div className="qdv-empty-inline">No attempts yet — start your first quiz!</div>
          ) : (
            <div className="qdv-attempts-list">
              {attempts.map((attempt, idx) => {
                const isCompleted = !!(attempt.completedAt || attempt.completed_at || attempt.scorePercent != null || attempt.score_percent != null);
                const score = attempt.scorePercent ?? attempt.score_percent;
                const dateStr = attempt.completedAt || attempt.createdAt || attempt.startedAt;

                return (
                  <div
                    key={attempt.id || idx}
                    className={`qdv-attempt-row ${onSelectAttempt ? 'qdv-attempt-row--clickable' : ''}`}
                    onClick={() => onSelectAttempt && onSelectAttempt(quiz, attempt, isCompleted ? 'review' : 'continue')}
                    role={onSelectAttempt ? 'button' : undefined}
                    tabIndex={onSelectAttempt ? 0 : undefined}
                    onKeyDown={onSelectAttempt ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectAttempt(quiz, attempt, isCompleted ? 'review' : 'continue'); } } : undefined}
                  >
                    <div className="qdv-attempt-info">
                      <span className="qdv-attempt-date">{formatShortDate(dateStr)}</span>
                      <span className="qdv-attempt-time">{formatTime(dateStr)}</span>
                    </div>
                    <div className="qdv-attempt-trailing">
                      {isCompleted && score != null ? (
                        <span className={`qdv-attempt-score ${getScoreClass(score)}`}>
                          {Math.round(score)}%
                        </span>
                      ) : (
                        <span className="qdv-attempt-status qdv-attempt-status--incomplete">In Progress</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function getScoreClass(score) {
  if (score >= 90) return 'qdv-score--excellent';
  if (score >= 70) return 'qdv-score--good';
  if (score >= 50) return 'qdv-score--fair';
  return 'qdv-score--poor';
}

export default QuizDetailView;
